import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { uploadOne, fileUrl } from "@/lib/uploads";
import {
  Landmark, ShieldCheck, Lock, FileText, Loader2, Send, Package,
  Sparkles, Copy, Download, CheckCircle2,
} from "lucide-react";

function StatusPill({ label, tone = "gray" }) {
  const t = {
    gold: "text-[#d4af37] border-[#d4af37]/40 bg-[#d4af37]/5",
    green: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    red: "text-red-300 border-red-500/40 bg-red-500/5",
    gray: "text-gray-400 border-gray-600/40 bg-gray-800/10",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${t}`}>
      {label}
    </span>
  );
}

export default function ClosurePage() {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/closure-queue");
      setProjects(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [selectedId]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/closure/projects/${selectedId}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [selectedId]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setRole(data.role?.name || data.subject_type);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); loadMe(); }, [load, loadMe]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const canFinance = useMemo(() => role === "Finance Manager" || role === "Finance Executive" || role === "admin" || role === "Director", [role]);
  const canOps = useMemo(() => role === "Operations Manager" || role === "Operations Executive" || role === "admin" || role === "Director", [role]);

  return (
    <div className="p-8 min-h-screen">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-1">Phase 9 · Closure & Warranty Handover</p>
          <h1 className="font-display text-3xl text-white">Ledger Verification Desk</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" strokeWidth={1.5} />
          {canFinance ? "Finance write mode" : canOps ? "Operations warranty mode" : "Read-only closure viewer"}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        <aside className="border border-[#262626] bg-[#0a0a0a] rounded-md h-fit">
          <p className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 border-b border-[#262626]">
            Closure Queue
          </p>
          {projects.length === 0 && (
            <p className="p-6 text-xs text-gray-500">No projects awaiting closure yet.</p>
          )}
          {projects.map((p) => {
            const isActive = p.id === selectedId;
            const cleared = !!p.final_balance_cleared;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                data-testid={`closure-project-${p.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#1c1c1c] transition-all ${
                  isActive ? "bg-[#d4af37]/5 border-l-2 border-l-[#d4af37]" : "hover:bg-[#131313]"
                }`}
              >
                <p className="text-sm text-white truncate">{p.client_name}{p.city ? ` · ${p.city}` : ""}</p>
                <p className="text-[11px] font-mono text-gray-500 truncate">
                  {p.project_name}
                </p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 truncate mt-0.5">
                  ₹{Number(p.settlement_balance_due || 0).toLocaleString("en-IN")} due
                </p>
                <div className="mt-2">
                  {cleared ? (
                    <StatusPill label="🔒 Closed" tone="green" />
                  ) : (
                    <StatusPill label="Awaiting Receipt" tone="gold" />
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        <section className="border border-[#262626] bg-[#0a0a0a] rounded-md min-h-[70vh]">
          {!selected && (
            <div className="p-12 text-center text-xs text-gray-500">Select a project on the left.</div>
          )}
          {selected && (
            <ClosureDetail
              project={selected}
              canFinance={canFinance}
              canOps={canOps}
              onChanged={() => { load(); loadDetail(); }}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function ClosureDetail({ project: p, canFinance, canOps, onChanged }) {
  const cleared = !!p.final_balance_cleared;
  const publicUrl = p.public_snag_token ? `${window.location.origin}/client-progress/${p.public_snag_token}` : null;

  return (
    <div>
      <div className="p-6 border-b border-[#262626]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            {cleared ? (
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
                <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37]">Master Status · PERMANENTLY CLOSED</p>
              </div>
            ) : (
              <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-gray-500 mb-1">Awaiting Final Receipt</p>
            )}
            <h2 className="font-display text-2xl text-white">{p.client_name}{p.city ? ` · ${p.city}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-1">{p.project_name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Contract: <span className="text-white font-mono">₹{Number(p.project_amount || 0).toLocaleString("en-IN")}</span>
              {" · "}Received: <span className="text-white font-mono">₹{Number(p.amount_received || 0).toLocaleString("en-IN")}</span>
            </p>
          </div>
          <div className="min-w-[260px] text-right">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Final Balance Due</p>
            <p className="font-display text-3xl text-[#d4af37]" data-testid="closure-balance">₹{Number(p.settlement_balance_due || 0).toLocaleString("en-IN")}</p>
            {p.settlement_sent_at && (
              <p className="text-[10px] text-gray-500 mt-1 font-mono">
                Statement sent {new Date(p.settlement_sent_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <FinanceCard project={p} canFinance={canFinance} onChanged={onChanged} />
        <WarrantyCard project={p} canOps={canOps} onChanged={onChanged} />
      </div>

      <div className="px-6 pb-8">
        <HandoverArchive project={p} publicUrl={publicUrl} />
      </div>
    </div>
  );
}

function FinanceCard({ project: p, canFinance, onChanged }) {
  const [txnRef, setTxnRef] = useState("");
  const [note, setNote] = useState("");
  const [taxFile, setTaxFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const cleared = !!p.final_balance_cleared;

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const rec = await uploadOne(f);
      setTaxFile(rec);
      toast.success("Tax clearance uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!txnRef.trim()) return toast.error("Transaction reference required");
    if (!taxFile) return toast.error("Tax clearance document required");
    setSubmitting(true);
    try {
      await api.post(`/finance/projects/${p.id}/final-receipt`, {
        transaction_reference_id: txnRef.trim(),
        tax_clearance_file_id: taxFile.id,
        note: note.trim() || null,
      });
      toast.success("Final receipt logged · project CLOSED · handover engine triggered");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#262626] rounded-md bg-[#0f0f0f]">
      <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
        <Landmark className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white">1 · Finance Receipt</p>
        {cleared && <StatusPill label="Cleared" tone="green" />}
      </div>

      {cleared ? (
        <div className="p-6 space-y-2 text-xs" data-testid="finance-receipt-summary">
          <Row label="Transaction Ref" value={<span className="font-mono text-[#d4af37]">{p.transaction_reference_id}</span>} />
          <Row label="Cleared At" value={p.final_receipt_logged_at ? new Date(p.final_receipt_logged_at).toLocaleString() : "—"} />
          {p.tax_clearance_file_id && (
            <Row label="Tax Clearance" value={
              <a href={fileUrl(p.tax_clearance_file_id)} target="_blank" rel="noreferrer" className="text-[#d4af37] underline">Download ↗</a>
            } />
          )}
          {p.final_receipt_note && <Row label="Note" value={<span className="text-gray-300">{p.final_receipt_note}</span>} />}
        </div>
      ) : canFinance ? (
        <form onSubmit={submit} className="p-5 space-y-4" data-testid="finance-receipt-form">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Transaction Reference ID</label>
            <input
              className="iz-input w-full"
              placeholder="e.g. NEFT-2026-A1B2C3"
              value={txnRef}
              onChange={(e) => setTxnRef(e.target.value)}
              data-testid="txn-ref-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Tax Clearance Statement (PDF)</label>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={pickFile} data-testid="tax-file-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="iz-btn-secondary w-full flex items-center justify-center gap-2 text-xs"
              data-testid="tax-file-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {taxFile ? `Attached: ${taxFile.original_filename}` : "Upload tax clearance statement"}
            </button>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Note (optional)</label>
            <textarea className="iz-input w-full min-h-[60px]" value={note} onChange={(e) => setNote(e.target.value)} data-testid="finance-note" />
          </div>
          <button
            type="submit"
            disabled={submitting || !taxFile}
            className="iz-btn-primary w-full flex items-center justify-center gap-2 text-xs disabled:opacity-40"
            data-testid="confirm-receipt-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />}
            Confirm Receipt & Lock Project
          </button>
          <p className="text-[10px] text-gray-500 text-center">
            ⚠ Once locked, no further edits are permitted across any module.
          </p>
        </form>
      ) : (
        <p className="p-6 text-xs text-gray-500">Awaiting Finance to log the final receipt.</p>
      )}
    </div>
  );
}

function WarrantyCard({ project: p, canOps, onChanged }) {
  const [cert, setCert] = useState(null);
  const [terms, setTerms] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const cleared = !!p.final_balance_cleared;
  const uploaded = !!p.warranty_certificate_file_id;

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const rec = await uploadOne(f);
      setCert(rec);
      toast.success("Warranty certificate uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cert) return toast.error("Certificate PDF required");
    setSubmitting(true);
    try {
      await api.post(`/operations/projects/${p.id}/warranty-certificate`, {
        warranty_certificate_file_id: cert.id,
        warranty_terms_summary: terms.trim() || null,
      });
      toast.success(cleared ? "Warranty certificate uploaded · handover package dispatching" : "Certificate saved. Handover will trigger once Finance clears balance.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#262626] rounded-md bg-[#0f0f0f]">
      <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white">2 · Warranty Certificate</p>
        {uploaded && <StatusPill label="Uploaded" tone="green" />}
      </div>

      {uploaded ? (
        <div className="p-6 space-y-2 text-xs" data-testid="warranty-summary">
          <Row label="Uploaded" value={p.warranty_uploaded_at ? new Date(p.warranty_uploaded_at).toLocaleString() : "—"} />
          {p.warranty_terms_summary && <Row label="Terms" value={<span className="text-gray-300">{p.warranty_terms_summary}</span>} />}
          <Row label="Certificate" value={
            <a href={fileUrl(p.warranty_certificate_file_id)} target="_blank" rel="noreferrer" className="text-[#d4af37] underline">Download ↗</a>
          } />
        </div>
      ) : canOps ? (
        <form onSubmit={submit} className="p-5 space-y-4" data-testid="warranty-form">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Warranty Certificate (PDF)</label>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={pickFile} data-testid="warranty-file-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="iz-btn-secondary w-full flex items-center justify-center gap-2 text-xs"
              data-testid="warranty-file-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {cert ? `Attached: ${cert.original_filename}` : "Upload warranty certificate"}
            </button>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Warranty Terms Summary (optional)</label>
            <textarea className="iz-input w-full min-h-[60px]" placeholder="e.g. 12 months workmanship + 24 months structural"
                      value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="warranty-terms" />
          </div>
          <button
            type="submit"
            disabled={submitting || !cert}
            className="iz-btn-primary w-full flex items-center justify-center gap-2 text-xs disabled:opacity-40"
            data-testid="upload-warranty-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
            Save Warranty Certificate
          </button>
        </form>
      ) : (
        <p className="p-6 text-xs text-gray-500">Awaiting Operations to upload the certificate.</p>
      )}
    </div>
  );
}

function HandoverArchive({ project: p, publicUrl }) {
  const packageReady = !!p.handover_package_file_id;
  const status = p.handover_status;
  const publicZip = p.public_snag_token
    ? `${window.location.origin.replace(/\/$/, "")}${process.env.REACT_APP_BACKEND_URL ? "" : ""}/api/public/projects/${p.public_snag_token}/handover-package.zip`
    : null;
  const backendZip = p.public_snag_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${p.public_snag_token}/handover-package.zip`
    : null;

  const copyLink = () => {
    if (!backendZip) return;
    navigator.clipboard.writeText(backendZip);
    toast.success("Handover download link copied");
  };

  return (
    <div className="border border-[#262626] bg-[#0f0f0f] rounded-md mt-4">
      <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
        <Package className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white">3 · Handover Package Archive</p>
        {packageReady && <StatusPill label={status || "compiled"} tone="green" />}
        {!packageReady && status && <StatusPill label={status.replace(/_/g, " ")} tone="gold" />}
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-[1fr_260px] gap-6">
        <div className="space-y-2 text-xs">
          {!packageReady && (
            <p className="text-gray-500">
              The Handover Package will be compiled automatically once BOTH the Finance receipt is cleared AND the warranty certificate is uploaded.
            </p>
          )}
          {packageReady && (
            <>
              <Row label="Dispatched" value={p.handover_dispatched_at ? new Date(p.handover_dispatched_at).toLocaleString() : "—"} />
              <Row label="Email Status" value={<span className="font-mono text-[#d4af37]">{p.handover_email_status || "queued"}</span>} />
              {(p.archive_entries || []).map((e) => (
                <div key={e.id} className="text-xs border-t border-[#1c1c1c] pt-2 mt-2">
                  <p className="text-white">{e.title}</p>
                  <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">
                    {e.kind} · {new Date(e.created_at).toLocaleString()} · email: {e.email_status}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="space-y-2">
          {packageReady && backendZip && (
            <>
              <a
                href={backendZip}
                target="_blank"
                rel="noreferrer"
                download
                className="iz-btn-primary w-full flex items-center justify-center gap-2 text-xs"
                data-testid="handover-download-btn"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                Download ZIP
              </a>
              <button
                type="button"
                onClick={copyLink}
                className="iz-btn-secondary w-full flex items-center justify-center gap-2 text-xs"
                data-testid="handover-copy-btn"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                Copy client link
              </button>
            </>
          )}
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noreferrer"
               className="block text-center text-[11px] text-gray-500 hover:text-[#d4af37] mt-2"
               data-testid="closure-open-viewport">Open client viewport ↗</a>
          )}
        </div>
      </div>

      {packageReady && (
        <div className="border-t border-[#d4af37]/40 bg-[#d4af37]/5 px-6 py-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#d4af37]">
            Project archived · irreversible closure confirmed
          </p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[#1c1c1c] pb-1.5">
      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</span>
      <span className="text-xs text-gray-300 truncate">{value}</span>
    </div>
  );
}
